/**
 * MenusPage — Menus de la cantine, vue semaine.
 * - Lecture pour tout le staff (agent inclus) et les familles.
 * - Édition (saisie/modif/suppression) réservée manager + admin (peutGerer).
 * Clic sur un jour → dialog détail/édition (composantes, type, allergènes, note).
 */

import { useEffect, useMemo, useState } from 'react';
import { UtensilsCrossed, Plus, Trash2, AlertCircle, FileText, RefreshCw } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Field, Input, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { MenuSyncDialog } from '@/components/scolaire/MenuSyncDialog';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { MENU_TYPES, ALLERGENES_INCO } from '@roronoa-collectivites/shared-types';
import type { Menu, MenuType, AllergeneInco } from '@roronoa-collectivites/shared-types';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
function startOfWeek(d = new Date()): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dow = x.getDay() === 0 ? 7 : x.getDay(); x.setDate(x.getDate() - (dow - 1)); return x;
}
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

const EMPTY = { typeMenu: 'standard' as MenuType, entree: '', plat: '', accompagnement: '', laitage: '', dessert: '', allergenes: [] as AllergeneInco[], note: '', partBio: '', partEgalim: '' };

export function MenusPage() {
  const { user } = useAuth();
  const { confirm } = useConfirmDialog();
  const peutGerer = user ? ['super_admin', 'admin', 'manager'].includes(user.role) : false;

  const [weekStart, setWeekStart] = useState<Date>(startOfWeek());
  const [menus, setMenus] = useState<Menu[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Dialog édition
  const [editDate, setEditDate] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [egalim, setEgalim] = useState<{ pctBio: number | null; pctDurable: number | null; conformeBio: boolean | null; conformeDurable: boolean | null } | null>(null);

  async function ouvrirPdf() {
    setPdfBusy(true);
    try {
      const blob = await api.scolaire.menus.pdfBlob(ymd(weekStart));
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Impossible de générer le PDF');
    } finally { setPdfBusy(false); }
  }

  const weekDays = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  async function load() {
    setLoading(true);
    try {
      setMenus(await api.scolaire.menus.list(ymd(weekStart), ymd(addDays(weekStart, 4))));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
    // EGalim de la semaine (silencieux : si HACCP non connecté ou erreur, on n'affiche rien)
    if (peutGerer) {
      try {
        const eg = await api.scolaire.menus.sync.egalim(ymd(weekStart), ymd(addDays(weekStart, 4)));
        setEgalim(eg.configure ? eg : null);
      } catch { setEgalim(null); }
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weekStart]);

  const menuDuJour = (day: Date) => menus.find((m) => m.dateMenu === ymd(day)) ?? null;

  function ouvrirEdition(day: Date) {
    const date = ymd(day);
    const existing = menuDuJour(day);
    setForm(existing
      ? { typeMenu: existing.typeMenu, entree: existing.entree ?? '', plat: existing.plat ?? '', accompagnement: existing.accompagnement ?? '', laitage: existing.laitage ?? '', dessert: existing.dessert ?? '', allergenes: existing.allergenes, note: existing.note ?? '', partBio: existing.partBio != null ? String(existing.partBio) : '', partEgalim: existing.partEgalim != null ? String(existing.partEgalim) : '' }
      : EMPTY);
    setEditDate(date);
  }

  async function enregistrer() {
    if (!editDate) return;
    setSaving(true);
    try {
      await api.scolaire.menus.upsert(editDate, {
        typeMenu: form.typeMenu,
        entree: form.entree.trim() || null, plat: form.plat.trim() || null,
        accompagnement: form.accompagnement.trim() || null, laitage: form.laitage.trim() || null,
        dessert: form.dessert.trim() || null, allergenes: form.allergenes, note: form.note.trim() || null,
        partBio: form.partBio.trim() ? parseFloat(form.partBio.replace(',', '.')) : null,
        partEgalim: form.partEgalim.trim() ? parseFloat(form.partEgalim.replace(',', '.')) : null,
      });
      setEditDate(null); await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur d\'enregistrement'); }
    finally { setSaving(false); }
  }

  async function supprimer() {
    if (!editDate) return;
    const ok = await confirm({ title: 'Supprimer ce menu ?', description: `Menu du ${editDate}`, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    setSaving(true);
    try { await api.scolaire.menus.remove(editDate); setEditDate(null); await load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de suppression'); }
    finally { setSaving(false); }
  }

  function toggleAllergene(a: AllergeneInco) {
    setForm((f) => ({ ...f, allergenes: f.allergenes.includes(a) ? f.allergenes.filter((x) => x !== a) : [...f.allergenes, a] }));
  }

  const typeLabel = (t: MenuType) => MENU_TYPES.find((x) => x.value === t)?.label ?? t;
  const allergeneLabel = (a: AllergeneInco) => ALLERGENES_INCO.find((x) => x.value === a)?.label ?? a;

  return (
    <ScolaireLayout activeTab="menus">
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Menus de la cantine</h1>
            <p className="mt-1 text-sm text-ink-muted">
              {peutGerer ? 'Cliquez sur un jour pour saisir ou modifier le menu.' : 'Menus de la semaine. Cliquez sur un jour pour le détail.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {peutGerer && (
              <Button variant="secondary" size="sm" onClick={() => setSyncOpen(true)}>
                <RefreshCw className="h-4 w-4" aria-hidden="true" /> <span className="hidden sm:inline">Synchro HACCP</span>
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => void ouvrirPdf()} disabled={pdfBusy}>
              <FileText className="h-4 w-4" aria-hidden="true" /> <span className="hidden sm:inline">PDF de la semaine</span>
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-4">{error}</Alert>}

        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, -7)))}>← Préc.</Button>
          <span className="text-sm font-medium text-ink">Semaine du {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</span>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, 7)))}>Suiv. →</Button>
        </div>

        {egalim && (egalim.pctBio != null || egalim.pctDurable != null) && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-r-lg border border-border bg-surface/50 px-3 py-2 text-xs">
            <span className="font-medium text-ink">EGalim (cette période) :</span>
            {egalim.pctBio != null && (
              <span className={egalim.conformeBio ? 'text-success' : 'text-ink-muted'}>
                Bio {egalim.pctBio.toFixed(0)}%{egalim.conformeBio ? ' ✓' : ' (objectif 20%)'}
              </span>
            )}
            {egalim.pctDurable != null && (
              <span className={egalim.conformeDurable ? 'text-success' : 'text-ink-muted'}>
                · Durable {egalim.pctDurable.toFixed(0)}%{egalim.conformeDurable ? ' ✓' : ' (objectif 50%)'}
              </span>
            )}
            <span className="text-ink-subtle">— en valeur d'achat, source HACCP</span>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {weekDays.map((day, idx) => {
              const m = menuDuJour(day);
              const isToday = ymd(day) === ymd(new Date());
              return (
                <Card key={ymd(day)} className={isToday ? 'ring-1 ring-cantine/40' : undefined}>
                  <CardBody className="flex h-full flex-col gap-2 py-3">
                    <div className="flex items-center justify-between">
                      <span className={['text-sm font-semibold', isToday ? 'text-cantine-fg' : 'text-ink'].join(' ')}>
                        {JOURS[idx]} {day.getDate()}
                      </span>
                      {m && <Badge>{typeLabel(m.typeMenu)}</Badge>}
                    </div>
                    {m ? (
                      <div className="flex-1 space-y-1 text-xs text-ink-muted">
                        {m.entree && <p><span className="text-ink-subtle">Entrée :</span> {m.entree}</p>}
                        {m.plat && <p className="text-ink"><span className="text-ink-subtle">Plat :</span> {m.plat}</p>}
                        {m.accompagnement && <p>{m.accompagnement}</p>}
                        {m.laitage && <p><span className="text-ink-subtle">Laitage :</span> {m.laitage}</p>}
                        {m.dessert && <p><span className="text-ink-subtle">Dessert :</span> {m.dessert}</p>}
                        {m.allergenes.length > 0 && (
                          <p className="pt-1 text-[11px] text-cantine-fg">Allergènes : {m.allergenes.map(allergeneLabel).join(', ')}</p>
                        )}
                        {(m.partBio != null || m.partEgalim != null) && (
                          <p className="text-[11px] text-success">
                            {[m.partBio != null ? `Bio ${m.partBio}%` : null, m.partEgalim != null ? `EGalim ${m.partEgalim}%` : null].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {m.note && <p className="text-[11px] italic text-ink-subtle">{m.note}</p>}
                      </div>
                    ) : (
                      <p className="flex-1 text-xs text-ink-subtle">Aucun menu.</p>
                    )}
                    {peutGerer && (
                      <Button variant="ghost" size="sm" className="mt-1 self-start" onClick={() => ouvrirEdition(day)}>
                        {m ? 'Modifier' : <><Plus className="h-4 w-4" /> Saisir</>}
                      </Button>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Dialog édition (manager/admin) */}
      <Dialog
        open={!!editDate}
        onClose={() => setEditDate(null)}
        title={editDate ? `Menu du ${new Date(editDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}` : ''}
        size="lg"
        footer={
          <div className="flex w-full items-center justify-between">
            {menuDuJour(new Date(editDate ?? Date.now())) ? (
              <Button variant="ghost" onClick={() => void supprimer()} disabled={saving}><Trash2 className="h-4 w-4" /> Supprimer</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setEditDate(null)}>Annuler</Button>
              <Button variant="cantine" loading={saving} onClick={() => void enregistrer()}>Enregistrer</Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <Field label="Type de menu" htmlFor="m-type">
            <select id="m-type" value={form.typeMenu} onChange={(e) => setForm((f) => ({ ...f, typeMenu: e.target.value as MenuType }))}
              className="h-10 w-full rounded-r-md border border-border bg-surface px-3 text-sm text-ink">
              {MENU_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Entrée" htmlFor="m-entree"><Input id="m-entree" value={form.entree} onChange={(e) => setForm((f) => ({ ...f, entree: e.target.value }))} /></Field>
          <Field label="Plat principal" htmlFor="m-plat"><Input id="m-plat" value={form.plat} onChange={(e) => setForm((f) => ({ ...f, plat: e.target.value }))} /></Field>
          <Field label="Accompagnement" htmlFor="m-acc"><Input id="m-acc" value={form.accompagnement} onChange={(e) => setForm((f) => ({ ...f, accompagnement: e.target.value }))} /></Field>
          <Field label="Laitage" htmlFor="m-lait"><Input id="m-lait" value={form.laitage} onChange={(e) => setForm((f) => ({ ...f, laitage: e.target.value }))} /></Field>
          <Field label="Dessert" htmlFor="m-dess"><Input id="m-dess" value={form.dessert} onChange={(e) => setForm((f) => ({ ...f, dessert: e.target.value }))} /></Field>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink">Allergènes (INCO)</span>
            <div className="flex flex-wrap gap-1.5">
              {ALLERGENES_INCO.map((a) => {
                const on = form.allergenes.includes(a.value);
                return (
                  <button key={a.value} type="button" onClick={() => toggleAllergene(a.value)}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-colors',
                      on ? 'bg-cantine text-white' : 'border border-border text-ink-muted hover:bg-surface/70'].join(' ')}>
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Field label="Note (facultatif)" htmlFor="m-note"><Input id="m-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="ex. produits bio / locaux" /></Field>
          {/* EGalim / bio manuels — utiles pour les communes sans synchro HACCP.
              Laisser vide si HACCP fournit déjà ces indicateurs. */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Part bio (%) — facultatif" htmlFor="m-bio">
              <Input id="m-bio" type="number" min={0} max={100} value={form.partBio}
                onChange={(e) => setForm((f) => ({ ...f, partBio: e.target.value }))} placeholder="ex. 20" />
            </Field>
            <Field label="Part EGalim durable (%) — facultatif" htmlFor="m-egalim">
              <Input id="m-egalim" type="number" min={0} max={100} value={form.partEgalim}
                onChange={(e) => setForm((f) => ({ ...f, partEgalim: e.target.value }))} placeholder="ex. 50" />
            </Field>
          </div>
          <p className="text-xs text-ink-subtle">À renseigner seulement si vous n'utilisez pas la synchro HACCP (qui calcule ces parts automatiquement).</p>
        </div>
      </Dialog>

      <MenuSyncDialog open={syncOpen} onClose={() => setSyncOpen(false)} onImported={() => void load()} />
    </ScolaireLayout>
  );
}
